<template>
  <transition
    name="request__transition"
    mode="out-in"
    appear
  >
    <div class="request">
      <div class="request__header">
        <h5>Request access</h5>

        <div class="request__close">
          <UtilityIconButton
            size="md"
            variant="round"
            backgroundColor="#12130f"
            highlightColor="rgba(255,255,255,0.7)"
            highlightBackgroundColor="#070806"
            content="Close"
            v-tippy="{ theme: 'statzon', placement: 'left', arrow: true, animation: 'scale' }"
            @click.native="close()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M23 20.168l-8.185-8.187 8.185-8.174-2.832-2.807-8.182 8.179-8.176-8.179-2.81 2.81 8.186 8.196-8.186 8.184 2.81 2.81 8.203-8.192 8.18 8.192z"/></svg>
          </UtilityIconButton>
        </div>
      </div>

      <div
        v-if="!errorMessage"
        class="request__info"
      >
        <p>To request access, please fill out the form below.<br />Our team will review your request promptly and get back to you shortly.</p>
      </div>

      <div class="request__form">
        <div
          v-if="errorMessage"
          class="row row--border"
        >
          <div class="row__full row__center">
            <p
              class="row__error"
              v-html="errorMessage"
            />
          </div>
        </div>

        <div :class="['row', errorMessage ? 'row--separator' : '']">
          <div class="row__full">
            <FormInput
              :value="email"
              ref-name="email"
              placeholder="you@company.com"
              label="Email"
              alt-label="Use your company/organization email"
              type="email"
              class="form__input"
              autocomplete="email"
              iconSize="md"
              :icon="icon.email"
              :error="errors.email"
              :status="status.email"
              @inputChanged="setFieldValue($event, 'email')"
              @unfocused="validate('email')"
            />
          </div>
        </div>

        <div class="row">
          <div class="row__full">
            <FormInput
              :value="firstName"
              ref-name="firstName"
              placeholder="John"
              label="First name"
              alt-label="Please fill in your first name"
              type="text"
              class="form__input"
              autocomplete="given-name"
              iconSize="md"
              :icon="icon.firstName"
              :error="errors.firstName"
              :status="status.firstName"
              @inputChanged="setFieldValue($event, 'firstName')"
              @unfocused="validate('firstName')"
            />
          </div>
        </div>

        <div class="row">
          <div class="row__full">
            <FormInput
              :value="lastName"
              ref-name="lastName"
              placeholder="Doe"
              label="Last name"
              alt-label="Please fill in your last name"
              type="text"
              class="form__input"
              autocomplete="family-name"
              iconSize="md"
              :icon="icon.lastName"
              :error="errors.lastName"
              :status="status.lastName"
              @inputChanged="setFieldValue($event, 'lastName')"
              @unfocused="validate('lastName')"
            />
          </div>
        </div>

        <div class="row row--border">
          <div class="row__full">
            <FormInput
              :value="phone"
              ref-name="phone"
              placeholder="Use full country code"
              label="Phone"
              alt-label="Please fill in your phone number"
              type="text"
              class="form__input"
              autocomplete="phone"
              iconSize="md"
              :icon="icon.phone"
              :error="errors.phone"
              :status="status.phone"
              @inputChanged="setFieldValue($event, 'phone')"
              @unfocused="validate('phone')"
            />
          </div>
        </div>

        <div class="row row--last">
          <div class="row__full row__center">
            <FormSubmit
              :status="formStatus"
              :disabled="false"
              variant="blue"
              @click="submit()"
            >
              {{ formStatus === 'success' ? 'Thank you!' : 'Request Access' }}
            </FormSubmit>
          </div>
        </div>

        <div class="row row--info">
          <div class="row__full row__notice noOverflow">
            Our team will respond to you within one working day.
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import FormInput from '@/components/form/Input.vue'
import FormSelect from '@/components/form/Select.vue'
import FormButton from '@/components/form/Button.vue'
import FormCheckbox from '@/components/form/Checkbox.vue'
import FormSubmit from '@/components/form/Submit.vue'
import UtilityFilter from '@/components/utility/Filter.vue'
import UtilityIconButton from '@/components/utility/IconButton.vue'
import config from '@/helpers/config'
import helperMethods from '@/helpers/utility'

export default {
  components: {
    FormInput,
    FormSelect,
    FormButton,
    FormCheckbox,
    FormSubmit,
    UtilityFilter,
    UtilityIconButton
  },
  props: {
    resourceType: {
      type: String,
      default: '',
      validator (values) {
        return ['datasets', 'pdfs'].includes(values)
      }
    },
    resourceId: {
      type: [Number, String],
      default: false
    },
    resourcePath: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      form: false,
      formStatus: 'default',
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      errorMessage: '',
      errors: {
        email: false,
        firstName: false,
        lastName: false,
        phone: false
      },
      status: {
        email: false,
        firstName: false,
        lastName: false,
        phone: false
      },
      icon: {
        email: '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M12 12.713l-11.985-9.713h23.971l-11.986 9.713zm-5.425-1.822l-6.575-5.329v12.501l6.575-7.172zm10.85 0l6.575 7.172v-12.501l-6.575 5.329zm-1.557 1.261l-3.868 3.135-3.868-3.135-8.11 8.848h23.956l-8.11-8.848z"/></svg>',
        firstName: '<svg width="100%" height="100%" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd"><path d="M24 24h-24v-24h24v24zm-2-22h-20v20h20v-20zm-4.118 14.064c-2.293-.529-4.427-.993-3.394-2.945 3.146-5.942.834-9.119-2.488-9.119-3.388 0-5.643 3.299-2.488 9.119 1.064 1.963-1.15 2.427-3.394 2.945-2.048.473-2.124 1.49-2.118 3.269l.004.667h15.993l.003-.646c.007-1.792-.062-2.815-2.118-3.29z"/></svg>',
        lastName: '<svg width="100%" height="100%" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd"><path d="M24 0v24h-24v-24h24zm-6.118 16.064c-2.293-.529-4.427-.993-3.394-2.945 3.146-5.942.834-9.119-2.488-9.119-3.388 0-5.643 3.299-2.488 9.119 1.064 1.963-1.15 2.427-3.394 2.945-2.048.473-2.124 1.49-2.118 3.269l.004.667h15.993l.003-.646c.007-1.792-.062-2.815-2.118-3.29z"/></svg>',
        phone: '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M14.89 23.654c-7.367 3.367-18.802-18.86-11.601-22.615l2.107-1.039 3.492 6.817-2.082 1.026c-2.189 1.174 2.37 10.08 4.609 8.994.091-.041 2.057-1.007 2.064-1.011l3.521 6.795c-.008.004-1.989.978-2.11 1.033zm-1.538-13.409l2.917.87c.223-.747.16-1.579-.24-2.317-.399-.739-1.062-1.247-1.808-1.469l-.869 2.916zm1.804-6.058c1.551.462 2.926 1.516 3.756 3.051.831 1.536.96 3.263.498 4.813l-1.795-.535c.325-1.091.233-2.306-.352-3.387-.583-1.081-1.551-1.822-2.643-2.146l.536-1.796zm.95-3.187c2.365.705 4.463 2.312 5.729 4.656 1.269 2.343 1.466 4.978.761 7.344l-1.84-.548c.564-1.895.406-4.006-.608-5.882-1.016-1.877-2.696-3.165-4.591-3.729l.549-1.841z"/></svg>'
      }
    }
  },
  computed: {
    isAuthenticated () {
      return this.$store.getters['user/isAuthenticated']
    },
    settings () {
      return this.$store.getters['app/getSettings']
    },
    userData () {
      return this.$store.getters['user/getData']
    },
    requests () {
      return this.$store.getters['user/getRequests']
    },
    totalRequests () {
      if (this.requests && this.requests.byId) {
        return Object.keys(this.requests.byId).length
      }

      return 0
    },
    selectedFilters () {
      return this.$store.getters['search/getSelectedFilters']
    }
  },
  methods: {
    async submit () {
      this.errorMessage = ''

      if (this.validate('all')) {
        this.formStatus = 'loading'

        let formData = {
          email: this.email,
          firstname: this.firstName,
          lastname: this.lastName,
          phone: this.phone,
          resource_type: this.resourceType,
          resource_id: this.resourceId,
          hubspotutk: this.getCookie('hubspotutk')
        }

        try {
          let response = await this.$store.dispatch('user/requestAccess', formData)

          if (response.data.status === 'ok') {
            this.formStatus = 'success'
            this.sendGoogleAnalyticsEvent('access_request', this.resourcePath, false, this.$store.getters['user/getSubscriptionStatus'])
            setTimeout(() => this.$emit('submitted', true), 1250)
          }
        } catch (error) {
          if (error.data) {
            if (error.data.errors) {
              this.errors = error.data.errors
              error.data.errors.forEach(err => {
                this.errorMessage += err.title + '<br>'
              })
            }
          }

          this.formStatus = 'error'
        }
      }
    },
    setFieldValue (value, field) {
      this[field] = value
    },
    setTypesValue (value, field) {
      this.types[field] = value
    },
    formatTypes () {
      let types = []

      for (const [key, value] of Object.entries(this.types)) {
        if (value === true) {
          types.push(key)
        }
      }

      return types.join(',')
    },
    validate (field) {
      let valid = true

      if (field === 'email' || field === 'all') {
        if (this.email === '') {
          this.errors.email = 'Required field'
          valid = false
        } else if (this.email.length > 255) {
          this.errors.email = 'Maximum of 255 characters'
          valid = false
        } else if (this.email !== '') {
          let emailTest = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          if (!emailTest.test(this.email)) {
            this.errors.email = 'Email must be valid'
            valid = false
          } else {
            this.errors.email = undefined
          }
        } else {
          this.errors.email = undefined
        }

        this.status.email = this.errors.email ? 'invalid' : 'valid'
      }

      if (field === 'firstName' || field === 'all') {
        if (this.firstName === '') {
          this.errors.firstName = 'Required field'
          valid = false
        } else if (this.firstName.length > 64) {
          this.errors.firstName = 'Maximum of 64 characters'
          valid = false
        } else {
          this.errors.firstName = undefined
        }

        this.status.firstName = this.errors.firstName ? 'invalid' : 'valid'
      }

      if (field === 'lastName' || field === 'all') {
        if (this.lastName === '') {
          this.errors.lastName = 'Required field'
          valid = false
        } else if (this.lastName.length > 64) {
          this.errors.lastName = 'Maximum of 64 characters'
          valid = false
        } else {
          this.errors.lastName = undefined
        }

        this.status.lastName = this.errors.lastName ? 'invalid' : 'valid'
      }

      if (field === 'phone' || field === 'all') {
        if (this.phone === '') {
          this.errors.phone = 'Required field'
          valid = false
        } else if (this.phone.length > 64) {
          this.errors.phone = 'Maximum of 64 characters'
          valid = false
        } else {
          this.errors.phone = undefined
        }

        this.status.phone = this.errors.phone ? 'invalid' : 'valid'
      }

      return valid
    },
    isFilterDisabled (value) {
      let view = this.getSearchView()

      if (value.type === 'queries') {
        return false
      }

      if (config.filterFields[view]) {
        for (const filter of config.filterFields[view]) {
          if (value.type === filter.key) {
            return false
          }
        }
      }

      return true
    },
    close () {
      this.$emit('closed')
    },
    ...helperMethods
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.request {
  @include font('content--md', $white, 0.9);
  position: absolute;
  top: 0px;
  width: 100%;
  height: auto;
  text-align: center;
  height: auto;
  background-color: $bg-3;
  border: 1px solid $black;

  @media screen and (min-width: $breakpointSm) {
    width: 448px;
  }

  @media screen and (min-width: $breakpointLg) {
    @include font('content--lg', $white, 0.9);
  }

  &__transition {
    &-enter-active, &-leave-active {
      transition: opacity 0.25s ease-out, transform 0.25s ease-out;
      transform: scale(1);
    }

    &-enter, &-leave-to {
      opacity: 0;
      transform: translateY(-20%) scale(0.6);
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    background-color: $bg-2;
    padding: 8px 12px 8px 16px;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;

    h5 {
      @include font('header--md');
    }
  }

  &__top {
    display: flex;
    justify-content: space-between;
    padding: 0.4rem 1rem;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;

    &--light {
      color: rgba($white, 0.7);
    }

    &--danger {
      color: $danger;
    }

    &--warning {
      color: $warning;
    }
  }

  &__info {
    margin: 0rem auto;
    padding: 0.75rem 1.5rem 1.25rem 1.5rem;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    color: rgba($white, 0.9);

    p {
      margin: 0;
      padding: 0;
    }
  }

  &__form {
    border-top: 1px solid rgba($white, 0.1);
  }

  &__tooltip {
    svg {
      position: relative;
      top: 3px;
      left: 3px;
      width: 18px;
      height: 18px;
      fill: rgba($white, 0.7);

      @media screen and (min-width: $breakpointLg) {
        width: 20px;
        height: 20px;
        top: 4px;
      }

      &:hover {
        fill: rgba($blue, 1);
      }
    }
  }
}

.row {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin: 0.75rem auto;
  padding: 0rem 1rem;

  @media screen and (min-width: $breakpointLg) {
    margin: 1rem auto;
  }

  &--border {
    border-bottom: 1px solid $black;
    margin: 0.75rem auto 0 auto !important;
    padding-bottom: 1rem !important;
  }

  &--separator {
    border-top: 1px solid rgba($white, 0.1);
    padding: calc(1rem - 1px) 1rem 0 1rem;
    margin: 0 !important;

    &Top {
      margin-top: 0.5rem;
    }
  }

  &--last {
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    padding: calc(1.25rem - 1px) 1rem;
    margin: 0 auto;
  }

  &--info {
    border-top: 1px solid rgba($white, 0.1);
    padding: calc(1rem - 1px) 1rem;
    margin: 0 auto;
  }

  &__full {
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;

    &--filters {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
    }

    &--condensed {
      justify-content: space-around;
    }
  }

  &__left {
    flex: 0.2;
    text-align: left;
  }

  &__right {
    flex: 0.8;
    text-align: left;
  }

  &__notice {
    color: rgba($white, 0.5);
    margin: 0 auto 1rem auto;
    display: block;
    font-style: italic;
  }

  &__center {
    margin: 0 auto;
    display: block;
  }

  &__error {
    color: $red;
    margin: 0;
  }

  &__label {
    color: rgba($white, 0.9);
    text-align: left;
  }

  &__operator {
    @include font('content--xs', $white, 0.5);
    position: relative;
    z-index: 9;
    font-weight: 500;
    margin: 0px 3px;
    text-transform: uppercase;
    display: inline-flex;

    /deep/ span {
      color: rgba($white, 0.7) !important;
    }

    &--active {
      color: rgba($white, 0.7);
    }
  }

  &__filter {
    position: relative;
    z-index: 10;
  }
}

.noOverflow {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

</style>
